import * as React from 'react';
import type { DonutSeg } from '../../domain/packageBreakdowns/types';

export interface DonutChartProps {
  segs: DonutSeg[];
  size?: number;
  strokeWidth?: number;
  hoverKey?: string | null;
  onHover?: (key: string | null) => void;
  tooltipByKey?: Map<string, string>;
}

export const DonutChart: React.FC<DonutChartProps> = ({
  segs,
  size = 170,
  strokeWidth = 18,
  hoverKey,
  onHover,
  tooltipByKey,
}) => {
  const ACTIVE_EXTRA = 3;
  const PAD = 2;
  const maxStroke = strokeWidth + ACTIVE_EXTRA;

  const r = Math.max(0, (size - maxStroke) / 2 - PAD);

  const c = 2 * Math.PI * r;
  const cx = size / 2;
  const cy = size / 2;

  let acc = 0;

  return (
    <div style={{ width: size, height: size, flex: '0 0 auto' }}>
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        style={{ display: 'block' }}
      >
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          stroke="rgba(0,0,0,0.06)"
          strokeWidth={strokeWidth}
        />

        <g transform={`rotate(-90 ${cx} ${cy})`}>
          {segs.map((s) => {
            const dash = (s.percent / 100) * c;
            const dasharray = `${dash} ${c - dash}`;
            const dashoffset = -((acc / 100) * c);
            acc += s.percent;

            const tt = tooltipByKey?.get(s.key);
            const interactive = !!tt && s.key !== '__remainder__';

            const isActive = !!hoverKey && s.key === hoverKey;
            const dimOthers =
              !!hoverKey && s.key !== hoverKey && s.key !== '__remainder__';

            return (
              <circle
                key={s.key}
                cx={cx}
                cy={cy}
                r={r}
                fill="none"
                stroke={s.color}
                strokeWidth={isActive ? strokeWidth + ACTIVE_EXTRA : strokeWidth}
                strokeLinecap="butt"
                strokeDasharray={dasharray}
                strokeDashoffset={dashoffset}
                opacity={dimOthers ? 0.25 : 1}
                pointerEvents="stroke"
                style={{
                  cursor: interactive ? 'pointer' : 'default',
                  transition: 'opacity 120ms ease, stroke-width 120ms ease',
                }}
                onMouseEnter={interactive ? () => onHover?.(s.key) : undefined}
                onMouseLeave={interactive ? () => onHover?.(null) : undefined}
              >
                {tt ? <title>{tt}</title> : null}
              </circle>
            );
          })}
        </g>
      </svg>
    </div>
  );
};